import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DetailInfo from "./DetailInfo";
import DetailInfoEditable from "./DetailInfoEditable";
import { Badge, Button, ButtonGroup, Icon, IconButton } from "../../../../components/ui";
import { isMobile } from "react-device-detect";
import { useAlert } from "react-alert";
import DetailInfoEditableRight from "./DetailInfoEditableRight";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

// Same-origin proxy on the Laravel backend — see useTradeImage.js for
// rationale (production CORS block on duckdns).
const whatappFetchUrl = "/api/integrations/daowhatsapp";

const ConfidenceDonut = ({ value }) => {
  const pct = Math.min(Math.max(value, 0), 100);
  const r = 32;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const isLow = pct < 50;
  const id = `conf-grad-${Math.random().toString(36).slice(2, 8)}`;
  const strokeColor = isLow ? "#FF9451" : `url(#${id})`;
  const textColor = isLow ? "#FF9451" : "#B7B7B7";
  return (
    <svg width="80" height="80" viewBox="0 0 80 80">
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6D3FD3" />
          <stop offset="100%" stopColor="#9383B7" />
        </linearGradient>
      </defs>
      <circle cx="40" cy="40" r={r} fill="none" stroke="#242424" strokeWidth="4" />
      <circle cx="40" cy="40" r={r} fill="none" stroke={strokeColor} strokeWidth="4"
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 40 40)" />
      <text x="40" y="44" textAnchor="middle" fontSize="14" fontWeight="700"
        fontFamily="'SF Pro Display', -apple-system, sans-serif" fill={textColor}>
        {pct.toFixed(1)}%
      </text>
    </svg>
  );
};

const MatchCountDonut = ({ index, total }) => {
  const r = 32;
  const circ = 2 * Math.PI * r;
  const isOnly = total === 1;
  return (
    <svg width="80" height="80" viewBox="0 0 80 80">
      <circle cx="40" cy="40" r={r} fill="none" stroke="#242424" strokeWidth="4" />
      <circle cx="40" cy="40" r={r} fill="none"
        stroke={isOnly ? "#61C554" : "#9383B7"} strokeWidth="4"
        strokeDasharray={circ} strokeDashoffset={0} strokeLinecap="round" />
      <text x="40" y="44" textAnchor="middle" fontSize="14" fontWeight="700"
        fontFamily="'SF Pro Display', -apple-system, sans-serif"
        fill={isOnly ? "#61C554" : "#B7B7B7"}>
        {index}/{total}
      </text>
    </svg>
  );
};

const CompareSection = ({
  extractedData,
  matchedData,
  isNonMatch,
  fetchedMatches,
  productId,
  setActiveItem,
  confidence,
  isFetched,
  setIsFetched,
  setCompareLeftFormData,
  isRemoved,
  section,
}) => {
  const responsive = {
    desktop: { breakpoint: { max: 90000, min: 1024 }, items: 3 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 2 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };
  const [index, setIndex] = useState(0);
  const [matchList, setMatchList] = useState(!isFetched ? matchedData : fetchedMatches);
  const [possibleFeatures, setPossibleFeatures] = useState(null);
  const [model, setModel] = useState(extractedData.model);
  const [dialColor, setDialColor] = useState(extractedData.dial_color);
  const [braceletType, setBraceletType] = useState(extractedData.bracelet_type);
  const [size, setSize] = useState(extractedData.size);
  const [dialIndex, setDialIndex] = useState(extractedData.dial_index);
  const [referenceNumber, setReferenceNumber] = useState(extractedData.reference_number);
  const alert = useAlert();
  const [isRuning, setIsRuning] = useState(false);

  useEffect(() => {
    setCompareLeftFormData({ model, dialColor, referenceNumber });
  }, [model, dialColor, referenceNumber]);

  useEffect(() => {
    setModel(extractedData.model);
    setDialColor(extractedData.dial_color);
    setBraceletType(extractedData.bracelet_type);
    setSize(extractedData.size);
    setDialIndex(extractedData.dial_index);
    setReferenceNumber(extractedData.reference_number);
    setMatchList(matchedData);
    setIndex(0);
  }, [isRemoved]);

  useEffect(() => {
    if (isFetched && isNonMatch) setMatchList(fetchedMatches);
  }, [isFetched, isNonMatch, fetchedMatches]);

  const matchConfidence = useMemo(() => {
    return matchList[index]?.match_confidence ?? confidence ?? 0;
  }, [matchList, index, confidence]);

  const onPrevious = () => setIndex((prev) => Math.max(prev - 1, 0));
  const onNext = () => setIndex((prev) => Math.min(prev + 1, matchList.length - 1));

  const fetchPossibleFeatures = async (brandName) => {
    try {
      const { data } = await axios.get(`${whatappFetchUrl}/possible_features/${brandName}`);
      setPossibleFeatures(data);
    } catch (error) {
      console.error("Failed to fetch possible features", error);
    }
  };

  const onRun = async () => {
    setIsRuning(true);
    const payload = {
      brand: extractedData.brand, model, dial_color: dialColor,
      bracelet_type: braceletType, dial_index: dialIndex, size,
      reference_no: referenceNumber ?? "",
    };
    // A failed run (rejected 4xx such as the upstream 424, or a 5xx/network
    // drop the global interceptor resolves as `undefined`) leaves the current
    // matches on screen; the interceptor has already told the user, and the
    // button returning to "Run" is the retry.
    try {
      const response = await axios.post(`${whatappFetchUrl}/run_matcher`, payload);
      if (!response) return;
      const { data } = response;
      if (data.total_candidates > 0) {
        setMatchList([data.best_match, ...data.top_matches]);
      } else {
        setMatchList([{}]);
        alert.show("No matches found");
      }
      setIsFetched(false);
      setIndex(0);
    } catch (error) {
      console.error("Failed to run the matcher", error);
    } finally {
      setIsRuning(false);
    }
  };

  useEffect(() => {
    extractedData?.brand && fetchPossibleFeatures(extractedData.brand);
  }, []);

  useEffect(() => {
    setActiveItem && setActiveItem(matchList[index]);
  }, [matchList, index]);

  const runButton = (className = "") => (
    <Button variant="secondary" data-btn="extraction-run" className={className} onClick={onRun}>
      {isRuning ? "Running..." : "Run"}
    </Button>
  );

  const candidateNav = (className = "") => (
    <ButtonGroup align="center" stackBelow={false} className={className}>
      <IconButton variant="ghost" label="Previous candidate" data-btn="extraction-candidate-prev" onClick={onPrevious}>
        <Icon name="ChevronLeft" />
      </IconButton>
      <IconButton variant="ghost" label="Next candidate" data-btn="extraction-candidate-next" onClick={onNext}>
        <Icon name="ChevronRight" />
      </IconButton>
    </ButtonGroup>
  );

  // === EXTRACTED SECTION: fields left, image+donut right ===
  if (section === "extracted") {
    return (
      <div className="cs-extracted">
        <div className="cs-extracted__fields">
          {possibleFeatures && (
            <DetailInfoEditable
              data={extractedData}
              model={model} setModel={setModel}
              dialColor={dialColor} setDialColor={setDialColor}
              dialIndex={dialIndex} setDialIndex={setDialIndex}
              braceletType={braceletType} setBraceletType={setBraceletType}
              size={size} setSize={setSize}
              referenceNumber={referenceNumber} setReferenceNumber={setReferenceNumber}
              possibleFeatures={possibleFeatures}
            />
          )}
        </div>
        <div className="cs-extracted__visual">
          {extractedData.reference_image_url && (
            <>
              <div className="cs-extracted__label">Detected from group</div>
              <img
                src={extractedData.reference_image_url}
                className="cs-extracted__img"
                alt="Detected watch"
              />
            </>
          )}
          <div className="cs-extracted__donut">
            <ConfidenceDonut value={matchConfidence} />
          </div>
          {runButton()}
        </div>
      </div>
    );
  }

  // === MATCHED SECTION: fields LEFT, image RIGHT (same as extracted) ===
  if (section === "matched") {
    return (
      <div className="cs-matched">
        <div className="cs-matched__details">
          {possibleFeatures && (
            !isFetched ? (
              <DetailInfo data={matchList[index]} />
            ) : (
              <DetailInfoEditableRight
                data={matchList[index]}
                possibleFeatures={possibleFeatures}
                setActiveItem={setActiveItem}
              />
            )
          )}
        </div>
        <div className="cs-matched__visual">
          <div className="cs-matched__label">Your watch</div>
          <img
            src={!isFetched ? matchList[index]?.reference_image_url : matchList[index]?.image_url}
            className="cs-matched__img"
            key={matchList[index]?.reference_image_url}
            alt="Your watch"
          />
          <div className="cs-matched__donut">
            <MatchCountDonut index={index + 1} total={matchList.length} />
          </div>
          {matchList.length > 1 && (
            candidateNav()
          )}
        </div>
      </div>
    );
  }

  // === FALLBACK: original layout for non-leads pages ===
  if (isMobile) {
    return (
      <div className="carousel carousel-mobile">
        <Carousel infinite responsive={responsive} autoPlay={false} removeArrowOnDeviceType={["desktop"]}>
          <div>
            {matchConfidence !== null && (
              <div className="row mr-1 ml-0"><div className="col-12 ps-0">
                <Badge tone="accent">
                  Confidence = {matchConfidence.toFixed(2)}%
                </Badge>
              </div></div>
            )}
            <div className="row align-items-stretch px-3">
              <div className="col-12 text-center">
                <img src={extractedData.reference_image_url} className="img-fluid" style={{ height: "255px", objectFit: "cover" }} />
              </div>
              <div className="mt-2 col-12">
                {possibleFeatures && (
                  <DetailInfoEditable data={extractedData} model={model} setModel={setModel}
                    dialColor={dialColor} setDialColor={setDialColor} dialIndex={dialIndex} setDialIndex={setDialIndex}
                    braceletType={braceletType} setBraceletType={setBraceletType} size={size} setSize={setSize}
                    referenceNumber={referenceNumber} setReferenceNumber={setReferenceNumber}
                    possibleFeatures={possibleFeatures} isMobile={isMobile} />
                )}
              </div>
              <div className="col-12 text-center">
                {runButton("mt-3")}
              </div>
            </div>
          </div>
          <div>
            <div className="row mr-0 ml-1"><div className="col-12 pe-0">
              <Badge tone={matchList.length === 1 ? "success" : "accent"}>
                {index + 1} / {matchList.length}
              </Badge>
            </div></div>
            <div className="row align-items-stretch px-3">
              <div className="col-12 text-center bg-dark">
                <img src={!isFetched ? matchList[index]?.reference_image_url : matchList[index]?.image_url}
                  className="img-fluid" style={{ height: "258px", objectFit: "cover" }} key={matchList[index]?.reference_image_url} />
              </div>
              <div className="col-12">
                {possibleFeatures && (!isFetched
                  ? <DetailInfo data={matchList[index]} isMobile={isMobile} />
                  : <DetailInfoEditableRight data={matchList[index]} possibleFeatures={possibleFeatures} setActiveItem={setActiveItem} isMobile={isMobile} />
                )}
              </div>
            </div>
            {matchList.length !== 1 && (
              candidateNav("mt-2 p-3")
            )}
          </div>
        </Carousel>
      </div>
    );
  }

  return (
    <div className={isMobile ? "" : "p-5"}>
      <div className="row">
        <div className="col-6">
          {matchConfidence !== null && (
            <div className="row"><div className="col-12 ps-0">
              <Badge tone="accent">Confidence = {matchConfidence.toFixed(2)}%</Badge>
            </div></div>
          )}
        </div>
        <div className="col-6">
          <div className="row"><div className="col-12 pe-0">
            <Badge tone={matchList.length === 1 ? "success" : "accent"}>
              {index + 1} / {matchList.length}
            </Badge>
          </div></div>
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          <div className="row align-items-stretch">
            <div className="col-6 text-center bg-dark">
              <img src={extractedData.reference_image_url} className="img-fluid" style={{ height: "400px", objectFit: "cover" }} />
            </div>
            <div className="col-6">
              {possibleFeatures && (
                <DetailInfoEditable data={extractedData} model={model} setModel={setModel}
                  dialColor={dialColor} setDialColor={setDialColor} dialIndex={dialIndex} setDialIndex={setDialIndex}
                  braceletType={braceletType} setBraceletType={setBraceletType} size={size} setSize={setSize}
                  referenceNumber={referenceNumber} setReferenceNumber={setReferenceNumber} possibleFeatures={possibleFeatures} />
              )}
            </div>
            <div className="col-12 text-center">
              {runButton("mt-3")}
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="row">
            <div className="col-6">
              {possibleFeatures && (!isFetched
                ? <DetailInfo data={matchList[index]} />
                : <DetailInfoEditableRight data={matchList[index]} possibleFeatures={possibleFeatures} setActiveItem={setActiveItem} />
              )}
            </div>
            <div className="col-6 text-center bg-dark">
              <img src={!isFetched ? matchList[index]?.reference_image_url : matchList[index]?.image_url}
                className="img-fluid" style={{ height: "400px", objectFit: "cover" }} key={matchList[index]?.reference_image_url} />
            </div>
          </div>
          {matchList.length !== 1 && (
            candidateNav("mt-2 p-3")
          )}
        </div>
      </div>
    </div>
  );
};

export default CompareSection;
